import React from "react"

import TopicLayout from "../shared/TopicLayout"
import {Arrow, Box, Diagram} from "../shared/Diagram"

export default function RateLimitingAndBackpressure() {
    return (
        <TopicLayout
            title="Rate limiting & backpressure"
            intro="Protecting a service from more work than it can handle: reject or delay excess requests at the edge with a rate limit, or let a slow consumer signal upstream to slow down with backpressure."
            diagram={
                <Diagram viewBox="0 0 560 210">
                    <text x={140} y={14} textAnchor="middle" fontSize={12} opacity={0.8}>Token bucket rate limiting</text>
                    <Box x={90} y={55} width={100} height={70} label="Token bucket" sublabel="● ● ●" accent/>
                    <Arrow x1={140} y1={30} x2={140} y2={55} label="refill N/s"/>
                    <Arrow x1={20} y1={80} x2={90} y2={80} label="request"/>
                    <Arrow x1={190} y1={70} x2={250} y2={55} label="allowed"/>
                    <Arrow x1={190} y1={105} x2={250} y2={120} label="429" dashed muted/>
                    <text x={140} y={175} textAnchor="middle" fontSize={11} opacity={0.7}>no token left: reject or delay the request</text>

                    <line x1={280} y1={10} x2={280} y2={190} stroke="var(--header-background)" opacity={0.25}/>

                    <text x={420} y={14} textAnchor="middle" fontSize={12} opacity={0.8}>Backpressure</text>
                    <Box x={300} y={40} width={90} height={34} label="Producer"/>
                    <Box x={420} y={40} width={110} height={34} label="Buffer (filling)" accent/>
                    <Box x={420} y={120} width={110} height={34} label="Consumer"/>
                    <Arrow x1={390} y1={57} x2={420} y2={57} label="writes"/>
                    <Arrow x1={475} y1={74} x2={475} y2={120} label="reads"/>
                    <Arrow x1={420} y1={140} x2={390} y2={60} label="slow down" dashed muted/>
                    <text x={420} y={175} textAnchor="middle" fontSize={11} opacity={0.7}>consumer signals before the buffer overflows</text>
                </Diagram>
            }
            useWhen={[
                'Rate limiting: a client or tenant could monopolize shared capacity, or a downstream dependency has a hard request-per-second ceiling that must never be exceeded.',
                'Backpressure: producer and consumer are connected directly or through a bounded queue, and a fast producer can outrun a slower consumer.'
            ]}
            tradeOff="Rate limiting protects the service but pushes the problem back onto the caller, which needs to handle 429s and retry-after headers gracefully. Token bucket allows bursts up to the bucket size while enforcing a long-run average; fixed windows are simpler but allow a burst at the window boundary. Backpressure keeps the system stable without dropping work, but an unbounded buffer just delays the failure and hides the real problem — the buffer itself needs a bound, and something has to happen when it fills: block, drop, or shed load."
            ruleOfThumb="Rate-limit at the edge with a token bucket sized for the real burst tolerance, and make every internal queue bounded with an explicit policy for what happens when it's full — never let an unbounded buffer stand in for capacity planning."
        />
    )
}
